/**
 * HoverTip — small hover/focus tooltip rendered through a portal on
 * document.body with fixed positioning, so it is never clipped by table or
 * scroll containers and always sits above the page content.
 *
 * Usage:
 *   <HoverTip text="שליחת הטופס למטופל">
 *     <button …><Icon /></button>
 *   </HoverTip>
 *
 * Wraps the child in an inline-flex span that owns the mouse/focus events.
 * On touch devices there is no hover: a LONG PRESS (~450ms) shows the tip
 * without firing the child's click; a plain tap behaves as usual.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./HoverTip.css";

const GAP = 8;
const LONG_PRESS_MS = 450;

export default function HoverTip({ text, children, placement = "top", className = "" }) {
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const pressTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const a = anchor.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let place = placement;
    let top = place === "top" ? a.top - t.height - GAP : a.bottom + GAP;
    if (place === "top" && top < 4) {
      place = "bottom";
      top = a.bottom + GAP;
    } else if (place === "bottom" && top + t.height > vh - 4) {
      place = "top";
      top = a.top - t.height - GAP;
    }

    let left = a.left + a.width / 2 - t.width / 2;
    left = Math.max(6, Math.min(left, vw - t.width - 6));

    setPos({ top, left, placement: place });
  }, [placement]);

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    const onScroll = () => setVisible(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(
    () => () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    },
    []
  );

  if (!text) return children;

  const clearPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  // Long press on touch: show the tip and swallow the click that would follow
  const handleTouchStart = () => {
    clearPress();
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      setIsTouch(true);
      setVisible(true);
    }, LONG_PRESS_MS);
  };

  const handleTouchEnd = () => {
    clearPress();
    if (isTouch && visible) {
      // Hide shortly after release so the text can actually be read
      setTimeout(() => {
        setVisible(false);
        setIsTouch(false);
      }, 1200);
    }
  };

  const handleClickCapture = (event) => {
    if (isTouch && visible) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    setVisible(false);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className={`ck-hovertip-anchor ${className}`}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onClickCapture={handleClickCapture}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={clearPress}
        onTouchMove={clearPress}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={tipRef}
            className={`ck-hovertip ck-hovertip--${pos.placement} ${isTouch ? "ck-hovertip--touch" : ""}`}
            style={{ top: pos.top, left: pos.left }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}
